import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription} from './ui/dialog';
import { Loader2, Camera, FileText, Star, AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN = () => localStorage.getItem('token');

/**
 * Post-Event Submissions Panel.
 *
 * Used on Admin Shift Detail + Client Event Recap pages.
 * Shows photos + written reports submitted by talent after the event.
 */
const PostEventSubmissionsPanel = ({ shiftId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/post-event/shift/${shiftId}`);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [shiftId]);

  useEffect(() => { fetch(); }, [fetch]);

  const imgSrc = (url) => {
    if (!url) return null;
    return url.startsWith('http') ? url : `${API}${url}?auth=${encodeURIComponent(TOKEN() || '')}`;
  };

  if (loading) {
    return (
      <Card><CardContent className="py-8 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-cyan-600" />
      </CardContent></Card>
    );
  }

  const photos = data?.photos || [];
  const reports = data?.reports || [];

  return (
    <Card className="bg-white border-gray-200" data-testid="post-event-submissions-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4 text-pink-600" />
            Post-Event Submissions
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline">{photos.length} photos</Badge>
            <Badge variant="outline">{reports.length} reports</Badge>
            <Button variant="ghost" size="sm" onClick={fetch}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {photos.length === 0 && reports.length === 0 && (
          <p className="text-sm text-gray-500 italic">No submissions yet.</p>
        )}

        {photos.length > 0 && (
          <div>
            <div className="text-xs uppercase text-gray-500 tracking-wide mb-2 flex items-center gap-1">
              <Camera className="w-3 h-3" /> Photos
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="post-event-photos-grid">
              {photos.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setLightbox(imgSrc(p.url))}
                  className="block aspect-square rounded overflow-hidden bg-gray-100 border border-gray-200 relative group"
                >
                  <img src={imgSrc(p.url)} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] px-1 py-0.5 truncate opacity-0 group-hover:opacity-100 transition">
                    {p.talent_name}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {reports.length > 0 && (
          <div>
            <div className="text-xs uppercase text-gray-500 tracking-wide mb-2 flex items-center gap-1">
              <FileText className="w-3 h-3" /> Reports
            </div>
            <div className="space-y-3" data-testid="post-event-reports-list">
              {reports.map((r) => (
                <div key={r.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {r.talent_photo ? (
                        <img src={r.talent_photo} alt="" className="w-7 h-7 rounded-full object-cover" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-gray-200" />
                      )}
                      <span className="font-medium text-sm">{r.talent_name}</span>
                      {r.overall_rating && (
                        <span className="flex items-center text-amber-500 text-xs">
                          <Star className="w-3 h-3 fill-amber-400" /> {r.overall_rating}/5
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-400">
                      {new Date(r.submitted_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{r.report_text}</p>
                  {r.issues_reported && (
                    <div className="mt-2 pt-2 border-t border-amber-200 bg-amber-50 -m-1 p-2 rounded text-xs text-amber-900">
                      <div className="flex items-center gap-1 font-medium mb-1">
                        <AlertTriangle className="w-3 h-3" /> Issues reported
                      </div>
                      <p className="whitespace-pre-wrap">{r.issues_reported}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>

      <Dialog open={!!lightbox} onOpenChange={(v) => !v && setLightbox(null)}>
        <DialogContent className="bg-black p-0 border-0 max-w-3xl" data-testid="post-event-lightbox">
          <DialogDescription className="sr-only">Dialog actions</DialogDescription>
          {lightbox && <img src={lightbox} alt="" className="w-full max-h-[85vh] object-contain" />}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default PostEventSubmissionsPanel;
